import { Link } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';

export default function Hero() {
  const { t } = useLanguage();

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-white">
      {/* Subtle background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-navy-50/50"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-brand-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 w-full px-6 sm:px-8 lg:px-16 xl:px-24 pt-32 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left content */}
          <div className="animate-slide-up">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-50 border border-brand-200 mb-8">
              <div className="w-2 h-2 rounded-full bg-brand-500 animate-pulse"></div>
              <span className="text-sm font-medium text-brand-700">{t('hero.badge')}</span>
            </div>

            {/* Title */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-tight">
              <span className="text-navy-800">{t('hero.title')}</span>
              <br />
              <span className="text-brand-500">{t('hero.titleHighlight')}</span>
            </h1>

            {/* Subtitle */}
            <p className="mt-8 text-xl text-navy-400 max-w-xl leading-relaxed">
              {t('hero.subtitle')}
            </p>

            {/* CTAs */}
            <div className="mt-10 flex flex-col sm:flex-row items-start gap-4">
              <Link to="/#get-started" className="btn-primary text-lg">
                {t('hero.cta')}
                <svg className="inline-block w-5 h-5 ml-2 rtl:mr-2 rtl:ml-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
              <a href="#how-it-works" className="px-8 py-4 text-lg font-semibold text-navy-700 border-2 border-navy-200 rounded-xl hover:border-navy-300 hover:bg-navy-50 transition-all duration-300">
                {t('hero.ctaSecondary')}
              </a>
            </div>

            {/* Stats */}
            <div className="mt-16 grid grid-cols-3 gap-8">
              <div>
                <div className="text-3xl font-bold text-navy-800">500+</div>
                <div className="text-sm text-navy-400 mt-1">{t('hero.stat1')}</div>
              </div>
              <div className="border-x border-navy-100 px-4">
                <div className="text-3xl font-bold text-navy-800">50K+</div>
                <div className="text-sm text-navy-400 mt-1">{t('hero.stat2')}</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-navy-800">99.9%</div>
                <div className="text-sm text-navy-400 mt-1">{t('hero.stat3')}</div>
              </div>
            </div>
          </div>

          {/* Right - Visual */}
          <div className="relative hidden lg:block animate-fade-in">
            {/* Main card mockup */}
            <div className="relative">
              <div className="bg-white rounded-3xl shadow-2xl shadow-navy-700/10 border border-gray-100 p-8">
                {/* Dashboard mockup */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  <div className="flex-1 h-6 bg-gray-100 rounded-md ml-4"></div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-navy-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-brand-500 flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-navy-700">{t('hero.dashboard.revenue')}</div>
                        <div className="text-xs text-navy-400">{t('hero.dashboard.thisMonth')}</div>
                      </div>
                    </div>
                    <div className="text-lg font-bold text-navy-800">12,450 TND</div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-4 bg-brand-50 rounded-xl">
                      <div className="text-2xl font-bold text-navy-800">156</div>
                      <div className="text-xs text-navy-400">{t('hero.dashboard.orders')}</div>
                    </div>
                    <div className="p-4 bg-navy-50 rounded-xl">
                      <div className="text-2xl font-bold text-navy-800">89%</div>
                      <div className="text-xs text-navy-400">{t('hero.dashboard.delivered')}</div>
                    </div>
                  </div>
                  <div className="h-24 bg-gradient-to-r from-brand-50 to-navy-50 rounded-xl flex items-end p-4 gap-1">
                    {[40, 65, 45, 80, 55, 90, 70, 95, 60, 85, 75, 100].map((h, i) => (
                      <div key={i} className="flex-1 bg-brand-500/80 rounded-t" style={{ height: `${h}%` }}></div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Floating notification */}
              <div className="absolute -top-4 -right-4 bg-white rounded-2xl p-4 shadow-xl border border-gray-100 animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-navy-700 text-sm font-semibold">{t('hero.dashboard.newOrder')}</div>
                    <div className="text-navy-400 text-xs">+89.99 TND</div>
                  </div>
                </div>
              </div>

              {/* Floating delivery */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-4 shadow-xl border border-gray-100 animate-float-delayed">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center">
                    <span className="text-lg">🚚</span>
                  </div>
                  <div>
                    <div className="text-navy-700 text-sm font-semibold">{t('hero.dashboard.delivered')}</div>
                    <div className="text-navy-400 text-xs">{t('hero.dashboard.orderNumber')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
